import React, { useContext } from "react";
import { ColProps_2, ColProps_3 } from "@core/utils/ColProps";
import { __t } from "@core/translation/translation";
import useStyles from "./styles";
import { Grid } from "@material-ui/core";
import Typography from "@components/Typography";
import Button from "@components/Button";
import { Link, useHistory, useParams } from "react-router-dom";
import clsx from "clsx";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import TabsPanel from "@components/TabsPanel";
import PriceFormat from "@components/PriceFormat";
import { StatsContext } from "@context/statsContext";
import { useMarkets } from "@context/marketsContext";
import {
  getCurrencyMeta,
  getMarketKey,
  getIconPath,
  getQuoteLabel,
  getQuotePriceSuffix,
} from "@core/utils/getCurrencyMeta";

/**
 * Dynamic market list — quote tabs and base currencies from GET /api/1/markets/catalog.
 *
 * @param {"sidebar"|"header"|"popover"} variant
 * @param {Function} [onNavigate] — called after item click (e.g. close popover)
 * @param {boolean} [showTradeMarketLink]
 */
const MarketListPanel = ({
  variant = "sidebar",
  onNavigate,
  showTradeMarketLink = variant !== "sidebar",
}) => {
  const classes = useStyles();
  const { statsState } = useContext(StatsContext);
  const { currencyName } = useParams();
  const history = useHistory();
  const { quoteCurrencies, marketsByQuote, isLoaded } = useMarkets();

  const isHeader = variant === "header" || variant === "popover";
  const colTitle = isHeader ? ColProps_2 : ColProps_3;
  const colPrice = isHeader ? ColProps_2 : { xs: 8, sm: 8, md: 8, lg: 8, xl: 8 };

  const navigateToMarket = (base, quote) => {
    history.push(`/market/${base}-${quote}`);
    onNavigate?.();
  };

  const renderRow = (base, quote) => {
    const route = `${base}-${quote}`;
    const meta = getCurrencyMeta(base);
    const marketKey = getMarketKey(base, quote);
    const price = statsState?.stats?.[marketKey]?.unitPrice;
    const selected = route === currencyName;

    const rowContent = (
      <>
        {isHeader && (
          <ListItemIcon>
            <img
              src={getIconPath(base)}
              alt={base}
              className={clsx(
                classes.currencyImage,
                selected && classes.selectedCurreny
              )}
            />
          </ListItemIcon>
        )}
        <ListItemText
          primary={
            <div className={classes.currencyText}>
              <Typography isBold variant={isHeader ? undefined : "3xs"}>
                {meta.name}
              </Typography>
              <Typography
                variant={isHeader ? "2xs" : "3xs"}
                className={classes.currencyUnit}
                color="secondary.tint40"
              >
                ({meta.unit})
              </Typography>
            </div>
          }
        />
        <Grid item {...colPrice} container justify="center">
          <Grid item className={isHeader ? classes.currencyPrice : undefined}>
            <ListItemText className={classes.ListItemTextPrice}>
              <Typography variant={isHeader ? undefined : "3xs"}>
                <PriceFormat price={price} />
              </Typography>
            </ListItemText>
            {isHeader && (
              <ListItemText className={classes.ListItemTextIcon}>
                <span
                  className={clsx(
                    selected ? "icon-check" : "icon-chevron-left",
                    classes.iconChevronLeft
                  )}
                />
              </ListItemText>
            )}
          </Grid>
        </Grid>
      </>
    );

    if (isHeader) {
      return (
        <Link key={route} to={`/market/${route}`}>
          <ListItem
            button
            selected={selected}
            onClick={onNavigate}
            className={classes.currencyItem}
          >
            {rowContent}
          </ListItem>
        </Link>
      );
    }

    return (
      <ListItem
        key={route}
        className={classes.currencyItem}
        button
        selected={selected}
        onClick={() => navigateToMarket(base, quote)}
      >
        {rowContent}
      </ListItem>
    );
  };

  const drawMarket = (quote) => {
    const bases = marketsByQuote[quote] || [];

    return (
      <Grid
        container
        direction="column"
        className={isHeader ? classes.tabContainer : classes.root}
        spacing={isHeader ? 10 : 0}
      >
        <Grid
          item
          container
          direction="row"
          className={
            isHeader ? classes.titleContainerHeader : classes.titleContainer
          }
        >
          <Grid item {...colTitle} container justify="center">
            <Typography color="secondary.tint40" variant={isHeader ? undefined : "3xs"}>
              {__t("Market")}
            </Typography>
          </Grid>
          <Grid item {...colPrice} container justify="center">
            <Typography color="secondary.tint40" variant={isHeader ? undefined : "3xs"}>
              {__t("Last Price")}
            </Typography>
            <Typography
              color="secondary.tint40"
              variant={isHeader ? undefined : "3xs"}
              className={isHeader ? classes.margin : undefined}
            >
              ({getQuotePriceSuffix(quote)})
            </Typography>
          </Grid>
        </Grid>

        {!isLoaded && (
          <Typography className={classes.emptyState} color="secondary.tint40">
            {__t("Loading...")}
          </Typography>
        )}

        {isLoaded && bases.length === 0 && (
          <Typography className={classes.emptyState} color="secondary.tint40">
            {__t("No markets available")}
          </Typography>
        )}

        <List component="nav" className={classes.currencyItems}>
          {bases.map((base) => renderRow(base, quote))}
        </List>

        {showTradeMarketLink && (
          <Grid item>
            <Link to="/trade-market">
              <Button
                variant="contained"
                color="default"
                size="small"
                onClick={onNavigate}
              >
                <Typography>{__t("Exfa Markets")}</Typography>
                <span className={clsx("icon-chevron-left", classes.icon)} />
              </Button>
            </Link>
          </Grid>
        )}
      </Grid>
    );
  };

  if (!isLoaded) {
    return (
      <Typography className={classes.emptyState} color="secondary.tint40">
        {__t("Loading...")}
      </Typography>
    );
  }

  if (quoteCurrencies.length === 0) {
    return (
      <Typography className={classes.emptyState} color="secondary.tint40">
        {__t("No markets available")}
      </Typography>
    );
  }

  const tabs = quoteCurrencies.map((quote) => ({
    label: getQuoteLabel(quote),
    component: drawMarket(quote),
  }));

  return (
    <TabsPanel
      tabs={tabs}
      className={classes.appBar}
      size={isHeader ? undefined : "small"}
      smallTabs={true}
    />
  );
};

export default MarketListPanel;
